import React from 'react'
import { J2Caution } from '@e3dc-react/icons'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { Box, MenuItem, Typography } from '@mui/material'
import { TemplatesModel } from '../models/pdf-template.model'
import { TemplateCategory } from '../api/dto/pdf-template-category.dto'
import { ChargingSessionExportFormatDto } from '../api/dto/charging-session-export-request.dto'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { getTemplateAvailableLanguage } from '../utils/get-template-language'
import { makeStyles } from 'tss-react/mui'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'
import useModals from 'src/Hooks/useModals'
import { currencyCodeToSymbol } from '../../shared'

const useStyles = makeStyles()(theme => ({
  menuItem: {
    minWidth: 200,
    fontSize: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  textContainer: {
    width: 190,
    minWidth: 0,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
}))

interface ExportMenuOptionProps {
  templates: TemplatesModel[]
  category: TemplateCategory
  format: ChargingSessionExportFormatDto
  label: string
  handleExportFile: (format: ChargingSessionExportFormatDto, availableLanguage: AppLang | undefined) => void
  handleCloseMenu: () => void
  currency?: string
}

/**
 * Single menu option component inside menu for export templates
 */
export const ExportMenuOption: React.FC<ExportMenuOptionProps> = ({
  templates,
  category,
  format,
  label,
  handleCloseMenu,
  handleExportFile,
  currency,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const { openReimbursementExportSessionsDialog } = useModals()

  /**
   * Mapper for translations keys for app languages
   * english, german and dutch are copied from tolgee platform, translation key common.languages
   * Example: common.languages.english, common.languages.german....
   */
  const languageTranslationKeysMapper: Partial<Record<AppLang, string>> = {
    en: 'english',
    de: 'german',
    nl: 'dutch',
  }

  const currentLanguage = getCurrentLanguage()
  const templateLanguage = getTemplateAvailableLanguage(category, templates)
  const currencyCode = currencyCodeToSymbol(currency ?? 'EUR')

  return (
    <MenuItem
      className={classes.menuItem}
      onClick={() => {
        if (format === ChargingSessionExportFormatDto.PdfReimbursement) {
          openReimbursementExportSessionsDialog({ handleExportFile, templateLanguage, currency: currencyCode })
        } else {
          handleExportFile(format, templateLanguage)
        }

        handleCloseMenu()
      }}
      disabled={!templateLanguage}
    >
      <Box className={classes.textContainer}>
        <Typography sx={{ whiteSpace: 'normal' }}>
          {label} {!templateLanguage && t('emobility.chargingSession.export-not-supported')}
        </Typography>
      </Box>

      {templateLanguage !== currentLanguage && (
        <OptionalTooltipWrapper
          titleKey={
            templateLanguage &&
            t('emobility.chargingSession.export-back-up-language', {
              backupLanguage: t(`common.languages.${languageTranslationKeysMapper[templateLanguage]}`),
            })
          }
          placement="top"
          sx={{ ml: 2 }}
        >
          <J2Caution />
        </OptionalTooltipWrapper>
      )}
    </MenuItem>
  )
}
